import { onlyNumbers } from '../utils/currency';

export interface NewCardFields {
	number: string;
	expiration: string;
	securityCode: string;
	holder: string;
	document: string;
}

export type NewCardField = keyof NewCardFields;

export class NewCardValidationError extends Error {
	name = 'NewCardValidationError';
}

export const emptyNewCardFields = (document = ''): NewCardFields => ({
	number: '',
	expiration: '',
	securityCode: '',
	holder: '',
	document,
});

const passesLuhn = (digits: string): boolean => {
	let sum = 0;

	for (let index = 0; index < digits.length; index++) {
		let digit = Number(digits[digits.length - 1 - index]);

		if (index % 2 === 1) {
			digit *= 2;
			if (digit > 9) digit -= 9;
		}

		sum += digit;
	}

	return sum % 10 === 0;
};

export const parseExpiration = (expiration: string): { month: string; year: string } | null => {
	const [month, year] = expiration.split('/').map(part => onlyNumbers(part ?? ''));

	if (!month || !year || month.length !== 2 || ![2, 4].includes(year.length)) return null;

	const monthNumber = Number(month);
	if (monthNumber < 1 || monthNumber > 12) return null;

	return { month, year: year.length === 2 ? `20${year}` : year };
};

const isFutureExpiration = (expiration: string, now: Date): boolean => {
	const parsed = parseExpiration(expiration);
	if (!parsed) return false;

	const lastDayOfMonth = new Date(Number(parsed.year), Number(parsed.month), 0, 23, 59, 59);

	return lastDayOfMonth.getTime() >= now.getTime();
};

export const validateNewCard = (fields: NewCardFields, now: Date = new Date()): NewCardField[] => {
	const invalid: NewCardField[] = [];
	const number = onlyNumbers(fields.number);
	const document = onlyNumbers(fields.document);

	if (number.length < 13 || number.length > 19 || !passesLuhn(number)) invalid.push('number');
	if (!isFutureExpiration(fields.expiration, now)) invalid.push('expiration');
	if (!/^\d{3,4}$/.test(onlyNumbers(fields.securityCode))) invalid.push('securityCode');
	if (fields.holder.trim().length < 2) invalid.push('holder');
	if (![11, 14].includes(document.length)) invalid.push('document');

	return invalid;
};

export const formatCardNumber = (value: string): string =>
	onlyNumbers(value)
		.slice(0, 19)
		.replace(/(\d{4})(?=\d)/g, '$1 ');

export const formatExpiration = (value: string): string => {
	const digits = onlyNumbers(value).slice(0, 4);

	return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};
